import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Toon } from "@/components/Toon";
import { partnerHref } from "@/lib/links";

const EASE = [0.76, 0, 0.24, 1];

const LINKS = [
  {
    id: "call",
    label: "Call",
    value: "(317) 707-6818",
    href: "tel:3177076818",
  },
  {
    id: "email",
    label: "Email",
    value: "info@callmegerard.com",
    href: "mailto:info@callmegerard.com",
  },
  {
    id: "calendly",
    label: "Schedule",
    value: "calendly.com/callmegerard",
    href: "https://calendly.com/callmegerard/lets-talk",
    external: true,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    value: "linkedin.com/in/gerardwiley",
    href: "https://www.linkedin.com/in/gerardwiley",
    external: true,
  },
  {
    id: "youtube",
    label: "YouTube",
    value: "youtube.com/callmegerard",
    href: "https://youtube.com/callmegerard",
    external: true,
  },
];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="mx-auto max-w-[90rem] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mb-6 flex items-center gap-4">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-xs uppercase tracking-[0.35em] text-steel"
        >
          Contact
        </motion.p>
        <Toon testId="contact-toon" variant="point" />
      </div>

      <motion.h2
        data-testid="contact-headline"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mb-16 font-serif text-[clamp(2.75rem,7vw,6rem)] font-medium leading-[1.02] tracking-tight text-fog"
      >
        <span className="block overflow-hidden pb-1">
          <motion.span
            className="block will-change-transform"
            variants={{
              hidden: { y: "110%" },
              show: { y: "0%", transition: { duration: 0.9, ease: EASE } },
            }}
          >
            When in need,
          </motion.span>
        </span>
        <span className="block overflow-hidden pb-2">
          <motion.span
            className="block italic text-steel will-change-transform"
            variants={{
              hidden: { y: "110%" },
              show: {
                y: "0%",
                transition: { duration: 0.9, ease: EASE, delay: 0.12 },
              },
            }}
          >
            CallMe📲Gerard.
          </motion.span>
        </span>
      </motion.h2>

      <div className="border-b border-white/10">
        {LINKS.map((l, i) => (
          <motion.a
            key={l.id}
            data-testid={`contact-link-${l.id}`}
            href={l.external ? partnerHref(l.href, l.label) : l.href}
            {...(l.external
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
            className="group flex items-center justify-between gap-6 border-t border-white/10 py-8 md:py-10"
          >
            <span className="font-serif text-3xl tracking-tight text-fog transition-[color,transform] duration-300 group-hover:translate-x-2 group-hover:text-steel md:text-5xl">
              {l.label}
            </span>
            <span className="flex items-center gap-4 text-right">
              <span className="hidden text-xs uppercase tracking-[0.25em] text-dim transition-colors duration-300 group-hover:text-ash sm:inline">
                {l.value}
              </span>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-dim transition-[transform,color] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-steel" />
            </span>
          </motion.a>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        className="mt-12 flex flex-wrap gap-4"
      >
        <a
          data-testid="contact-cta-coverage"
          href={partnerHref("https://ehpinc.com/?aff=102645", "Reduce Employer Costs")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
        >
          Reduce Employer Costs
          <ArrowUpRight className="h-4 w-4" />
        </a>
        <a
          data-testid="contact-cta-funding"
          href={partnerHref("https://go.mypartner.io/business-financing/?ref=001Qk00000o1V9sIAE", "Get Business Funding")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
        >
          Get Business Funding
          <ArrowUpRight className="h-4 w-4" />
        </a>
        <a
          data-testid="contact-cta-energy"
          href={partnerHref("https://calendly.com/callmegerard/lets-talk", "Get an Energy Quote")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
        >
          Get an Energy Quote
          <ArrowUpRight className="h-4 w-4" />
        </a>
        <a
          data-testid="contact-cta-processing"
          href={partnerHref("https://calendly.com/callmegerard/lets-talk", "Get a Processing Quote")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
        >
          Get a Processing Quote
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </motion.div>

      <motion.p
        data-testid="contact-hours"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.3 }}
        className="mt-10 text-xs uppercase tracking-[0.25em] text-dim"
      >
        Remote office hours — Monday to Friday, 8am–6pm (US/EST)
      </motion.p>
    </section>
  );
}
